/**
 * ProjectCard — 프로젝트 목록에서 사용하는 카드 컴포넌트
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  Globe, Clock, Play, CheckCircle2, Loader2, AlertCircle,
  Trash2, ExternalLink, MoreHorizontal,
} from 'lucide-react';

interface ProjectCardProps {
  project: {
    id: string;
    url: string;
    name: string;
    status: string;
    preferences: { video_length: string; language: string };
    created_at: string;
    updated_at: string;
  };
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onStart: (id: string) => void;
}

const STATUS_CONFIG: Record<string, {
  label: string;
  color: string;
  bg: string;
  icon: typeof CheckCircle2;
}> = {
  init: { label: '준비', color: 'text-gray-600', bg: 'bg-gray-100', icon: Clock },
  scouting: { label: '탐방 중', color: 'text-blue-600', bg: 'bg-blue-100', icon: Loader2 },
  planning: { label: '기획 중', color: 'text-purple-600', bg: 'bg-purple-100', icon: Loader2 },
  approval_gate: { label: '승인 대기', color: 'text-amber-600', bg: 'bg-amber-100', icon: AlertCircle },
  directing: { label: '장면 설계', color: 'text-orange-600', bg: 'bg-orange-100', icon: Loader2 },
  narrating: { label: '내레이션', color: 'text-green-600', bg: 'bg-green-100', icon: Loader2 },
  mastering: { label: '영상 생성', color: 'text-pink-600', bg: 'bg-pink-100', icon: Loader2 },
  qa_check: { label: '검수 중', color: 'text-indigo-600', bg: 'bg-indigo-100', icon: Loader2 },
  complete: { label: '완성', color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle2 },
  error: { label: '오류', color: 'text-red-600', bg: 'bg-red-100', icon: AlertCircle },
};

export function ProjectCard({ project, onSelect, onDelete, onStart }: ProjectCardProps) {
  const [showMenu, setShowMenu] = React.useState(false);
  const statusConf = STATUS_CONFIG[project.status] || STATUS_CONFIG.init;
  const StatusIcon = statusConf.icon;
  const isActive = ['scouting', 'planning', 'directing', 'narrating', 'mastering', 'qa_check'].includes(project.status);

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '방금';
    if (mins < 60) return `${mins}분 전`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}시간 전`;
    return `${Math.floor(hours / 24)}일 전`;
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-xl border p-4 hover:shadow-md transition-all cursor-pointer group',
        isActive ? 'border-blue-200 ring-1 ring-blue-100' : 'border-gray-100'
      )}
      onClick={() => onSelect(project.id)}
    >
      {/* Top Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Globe className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-gray-900 truncate">{project.name}</h3>
            <p className="text-xs text-gray-500 truncate">{project.url}</p>
          </div>
        </div>

        <div className="relative">
          <button
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition"
          >
            <MoreHorizontal className="w-4 h-4 text-gray-400" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-8 bg-white border rounded-lg shadow-lg py-1 z-10 w-36">
              <button
                onClick={(e) => { e.stopPropagation(); window.open(project.url, '_blank'); setShowMenu(false); }}
                className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
              >
                <ExternalLink className="w-3.5 h-3.5" /> 사이트 열기
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(project.id); setShowMenu(false); }}
                className="w-full px-3 py-2 text-sm text-left hover:bg-red-50 text-red-600 flex items-center gap-2"
              >
                <Trash2 className="w-3.5 h-3.5" /> 삭제
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center justify-between">
        <div className={clsx(
          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
          statusConf.bg, statusConf.color
        )}>
          <StatusIcon className={clsx('w-3.5 h-3.5', isActive && 'animate-spin')} />
          {statusConf.label}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{timeAgo(project.updated_at)}</span>

          {project.status === 'init' && (
            <button
              onClick={(e) => { e.stopPropagation(); onStart(project.id); }}
              className="px-2.5 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition flex items-center gap-1"
            >
              <Play className="w-3 h-3" /> 시작
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
